import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { saveAs } from 'file-saver';

export interface PdfPopupTab {
  label: string;
  key: string;
  params?: Record<string, string>;
}

export interface PdfPopupData {
  tabs: PdfPopupTab[];
  activeKey?: string;
}

interface TabState {
  loading: boolean;
  blob: Blob | null;
  filename: string;
}

@Component({
  selector: 'app-pdf-viewer-popup',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatTabsModule,
    PdfViewerModule,
  ],
  templateUrl: './pdf-viewer-popup.component.html',
  styleUrls: ['./pdf-viewer-popup.component.css'],
})
export class PdfViewerPopupComponent {
  activeIndex = 0;
  zoom = 0.75;
  state: Record<string, TabState> = {};
  // pdfjs detaches the underlying ArrayBuffer on render, so we rebuild a
  // fresh Uint8Array from the cached Blob every time we display a tab.
  displaySrc: Uint8Array | null = null;

  constructor(
    private http: HttpClient,
    public dialogRef: MatDialogRef<PdfViewerPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PdfPopupData,
  ) {
    const idx = data.tabs.findIndex(t => t.key === data.activeKey);
    this.activeIndex = idx >= 0 ? idx : 0;
    this.loadTab(this.activeIndex);
  }

  get activeTab(): PdfPopupTab { return this.data.tabs[this.activeIndex]; }
  get activeState(): TabState | undefined { return this.state[this.activeTab.key]; }

  onTabChange(i: number): void {
    this.activeIndex = i;
    this.displaySrc = null;
    const tab = this.data.tabs[i];
    const cached = this.state[tab.key];
    if (cached?.blob) {
      this.showCached(tab.key);
    } else if (!cached) {
      this.loadTab(i);
    }
  }

  private async showCached(key: string): Promise<void> {
    const blob = this.state[key]?.blob;
    if (!blob) return;
    const buf = await blob.arrayBuffer();
    if (this.activeTab.key === key) {
      this.displaySrc = new Uint8Array(buf);
    }
  }

  private loadTab(i: number): void {
    const tab = this.data.tabs[i];
    this.state[tab.key] = { loading: true, blob: null, filename: '' };

    let params = new HttpParams();
    for (const [k, v] of Object.entries(tab.params ?? {})) params = params.set(k, v);

    this.http.get(`/backend/popupPdf/${tab.key}`, {
      params, responseType: 'blob', observe: 'response',
    }).subscribe({
      next: res => {
        const blob = res.body!;
        const cd = res.headers.get('Content-Disposition') || '';
        const filename = cd.includes('filename=')
          ? cd.split('filename=')[1].replace(/"/g, '').trim()
          : `${tab.label}.pdf`;

        this.state[tab.key] = { loading: false, blob, filename };
        if (this.activeTab.key === tab.key) this.showCached(tab.key);
      },
      error: () => {
        this.state[tab.key] = { ...this.state[tab.key], loading: false };
      },
    });
  }

  zoomIn(): void { this.zoom = Math.min(this.zoom + 0.25, 3); }
  zoomOut(): void { this.zoom = Math.max(this.zoom - 0.25, 0.5); }

  onDownload(): void {
    const s = this.activeState;
    if (s?.blob) saveAs(s.blob, s.filename || 'document.pdf');
  }

  onClose(): void { this.dialogRef.close(); }
}
